import * as React from "react";
import { SheetSettledProvider } from "@/components/ui/sheetSettled";
import { useSheetSettled } from "@/hooks/use-sheet-settled";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { ChevronLeft } from "lucide-react";
import { cn } from "@/lib/utils";
import { useIsPhone } from "@/hooks/use-phone";
import { createTouchOpenAutoFocusHandler } from "@/lib/touchAutoFocus";
import { useSheetFitClass } from "@/hooks/use-sheet-fit";
import {
  SheetCloseButton,
  SheetGrabZone,
  SHEET_GRAB_ZONE_CLASS,
  SHEET_HEADER_CLASS,
  SHEET_BODY_CLASS,
  SHEET_FOOTER_CLASS,
  SHEET_MAX_H_CLASS,
  SHEET_CENTERED_MAX_H_CLASS,
  SHEET_ANIM_RISE,
  SHEET_ANIM_CENTERED,
  SHEET_OVERLAY_CLASS,
  type SheetFit,
} from "@/components/ui/sheetChrome";

/**
 * GlassSheet — the single shell behind every filter surface. Solid white:
 * a bottom sheet on mobile, a centered modal on tablet/desktop (or always
 * centered when `centered` is passed — the option/date popups). Chrome follows
 * the shared filter palette (ink #13314A, hair #E4EDF5, scrim rgba(15,39,68,…)).
 * Fonts/weights are intentionally left untouched.
 *
 * Va sobre Radix Dialog, como el resto de hojas. Antes era un `createPortal` a
 * mano con su propio listener de Escape y un `if (!open) return null`, y ese
 * return era justo el problema: al cerrar, la ventana DESAPARECÍA de golpe: no
 * había animación de salida porque el nodo se iba del árbol en el mismo
 * fotograma. Radix lo mantiene montado los 300 ms de la bajada.
 *
 * De regalo vienen el foco atrapado dentro, el bloqueo del scroll de detrás y
 * que Escape cierre SOLO la ventana de encima (con dos listeners de `window`,
 * abrir el desplegable de una dimensión y pulsar Escape cerraba las dos).
 */
interface GlassSheetProps {
  open: boolean;
  onClose: () => void;
  title: string;
  /**
   * Cuando se pasa, la cabecera enseña una flecha a la izquierda del título. Es para
   * las ventanas que se abren DENTRO de otro flujo y tienen sitio al que volver que no
   * es «cerrar» (el selector de Reportar plaga vuelve a Tarea por voz). Los filtros no
   * la pasan, así que su cabecera no cambia.
   */
  onBack?: () => void;
  /** Etiqueta accesible de la flecha de atrás. */
  backLabel?: string;
  /** Force the centered modal layout even on mobile (option/date popups). */
  centered?: boolean;
  /** Render above an already-open sheet (the option popup is a second overlay). */
  elevated?: boolean;
  footer?: React.ReactNode;
  /** Max width (px) of the centered modal; option popups pass a smaller value. */
  maxWidth?: number;
  /**
   * Cuánto ocupa en el teléfono (ver `SheetFit`). Por defecto `short`, y no
   * `auto`: esta carcasa nació para los FILTROS, que existen justamente para ir
   * viendo la lista de detrás — una hoja de filtros a pantalla completa deja de
   * hacer su trabajo. Quien quiera crecer, que lo pida.
   */
  fit?: SheetFit;
  /**
   * Clases sueltas para el PANEL. Es la vía para las hojas que saben de antemano
   * lo alto que quieren ser, en vez de pedir que las midan con `fit`.
   *
   * Existe por «Rango de Fecha» (29-08-2026): pasaba `fit="auto"`, y medir
   * significa colgar un ResizeObserver del cuerpo Y DE CADA HIJO más un
   * MutationObserver de todo el subárbol, cuyo callback lee `scrollHeight` y
   * fuerza un reflujo síncrono. Todo eso se instalaba y hacía su primera medida
   * MIENTRAS la hoja subía, que es justo donde se notaba el tirón en Android.
   * Un calendario tiene alto conocido: no hay nada que medir, solo que dejarle
   * sitio.
   */
  panelClassName?: string;
  children: React.ReactNode;
  /**
   * EL CUERPO NO SE MONTA MIENTRAS LA HOJA SUBE (29-08-2026).
   *
   * Pasando `skeleton` (o `loading`), la hoja enseña ese hueco hasta que se ha
   * posado y solo entonces monta a sus hijos. Es lo que separa una hoja que
   * «abre suave» de una que entra a tirones: no se puede animar lo que todavía
   * no existe, así que todo lo que haya dentro se construye ANTES de que la
   * hoja pueda moverse, y en un Android de gama baja eso son fotogramas
   * perdidos justo al principio.
   *
   * `loading` es para cuando además faltan DATOS: mantiene el esqueleto después
   * de la subida. NO retrasa la consulta —eso sería perder red a cambio de
   * nada—: la petición sale al abrir, como siempre, y lo único que espera es el
   * momento de pintarla. El razonamiento largo está en `use-sheet-settled.ts`.
   */
  skeleton?: React.ReactNode;
  /** Los datos todavía no están: el esqueleto sigue puesto después de subir. */
  loading?: boolean;
}

const CENTERED_PANEL: React.CSSProperties = {
  background: "#fff",
  border: "1px solid #E4EDF5",
  borderRadius: 24,
  boxShadow: "0 30px 80px rgba(15,39,68,0.30)",
};

const BOTTOM_PANEL: React.CSSProperties = {
  background: "#fff",
  border: "1px solid #E4EDF5",
  borderTopLeftRadius: 28,
  borderTopRightRadius: 28,
  boxShadow: "0 -16px 50px rgba(15,39,68,0.22)",
};

export function GlassSheet({ open, onClose, title, onBack, backLabel, centered, elevated, footer, maxWidth = 520, fit = "short", panelClassName, skeleton, loading = false, children }: GlassSheetProps) {
  /** ¿Ha terminado ya de subir? Ver la prop `skeleton`. */
  const settled = useSheetSettled(open);
  // Centered floating modal on tablet & desktop (≥768px); bottom sheet only on phones.
  const isPhone = useIsPhone();
  const asCentered = centered || !isPhone;

  // Centrada = ventana flotante, no hoja: crecer no significa nada ahí.
  const [bodyNode, setBodyNode] = React.useState<HTMLDivElement | null>(null);
  const fitClass = useSheetFitClass({ fit: asCentered ? "short" : fit, host: bodyNode });

  // El desplegable de una dimensión se abre ENCIMA de la hoja de filtros, así que
  // necesita su propia capa. No baja a z-50 con las demás ventanas a propósito:
  // el selector de Reportar plaga sale sobre VoiceModalShell, que no está
  // portalizado y vive justo en z-50.
  const zIndex = elevated ? 90 : 80;

  const header = (
    <div
      className={cn(
        "flex items-center justify-between shrink-0",
        SHEET_HEADER_CLASS,
        // Hueco para el tirador, que en la hoja inferior va en absoluto por
        // encima de la cabecera (antes ocupaba sitio en el flujo).
        !asCentered && "pt-8",
      )}
    >
      <div className="flex items-center gap-2 min-w-0 pr-3">
        {onBack && (
          <button
            type="button"
            onClick={onBack}
            aria-label={backLabel ?? "Atrás"}
            className="flex items-center justify-center rounded-full shrink-0 w-[30px] h-[30px] -ml-0.5 bg-[rgba(15,39,68,0.07)] hover:bg-[rgba(15,39,68,0.12)] text-[#2E4A63] active:scale-95 transition-all"
          >
            <ChevronLeft style={{ width: 17, height: 17 }} strokeWidth={2.4} />
          </button>
        )}
        {/* `asChild`: el título accesible de Radix es este mismo h2, sin envolverlo
            en otro nodo que rompería el truncado. */}
        <DialogPrimitive.Title asChild>
          <h2 className="font-['Poppins'] font-bold truncate" style={{ fontSize: 20, color: "#13314A" }}>
            {title}
          </h2>
        </DialogPrimitive.Title>
      </div>
      {/* El cromo (esta X y la rayita de abajo) vive ahora en ui/sheetChrome y lo
          comparte toda la app; esta ventana fue la referencia. */}
      <SheetCloseButton onClick={onClose} aria-label="Cerrar" />
    </div>
  );

  const footerEl = footer ? (
    <div
      className={cn(SHEET_FOOTER_CLASS, "shrink-0")}
      style={{ borderTop: "1px solid #EEF3F8", background: "#fff" }}
    >
      {footer}
    </div>
  ) : null;

  return (
    <DialogPrimitive.Root
      open={open}
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
    >
      <DialogPrimitive.Portal>
        {/* zIndex en línea: gana al z-50 que trae la clase compartida. */}
        <DialogPrimitive.Overlay className={SHEET_OVERLAY_CLASS} style={{ zIndex }} />
        <DialogPrimitive.Content
          onOpenAutoFocus={createTouchOpenAutoFocusHandler()}
          aria-describedby={undefined}
          className={cn(
            "fixed flex flex-col overflow-hidden focus:outline-none",
            asCentered
              ? // Centrada con translate (no con flex) porque la aparición de
                // SHEET_ANIM_CENTERED lleva esos mismos -50%/-48%: si el centrado
                // fuera de otra forma, entraría cruzando la pantalla en diagonal.
                "left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[calc(100vw-3rem)] " +
                SHEET_CENTERED_MAX_H_CLASS + " " + SHEET_ANIM_CENTERED
              : "inset-x-0 bottom-0 w-full " + SHEET_MAX_H_CLASS + " " + SHEET_ANIM_RISE,
            // Solo en la hoja inferior: en la ventana centrada el alto lo
            // manda SHEET_CENTERED_MAX_H_CLASS.
            !asCentered && panelClassName,
            // AL FINAL. Ver `useSheetFitClass`.
            fitClass,
          )}
          style={{
            ...(asCentered ? { ...CENTERED_PANEL, maxWidth } : BOTTOM_PANEL),
            zIndex,
          }}
        >
          {/* Solo en la hoja inferior: la rayita, que además arrastra para cerrar.
              En la ventana centrada no se pinta — y no debe: el arrastre escribe
              un translateY en línea que pisaría el translate del centrado. */}
          {!asCentered && <SheetGrabZone onClose={onClose} className={SHEET_GRAB_ZONE_CLASS} />}
          {header}
          <div
            ref={setBodyNode}
            className={cn(
              "flex-1 min-h-0 overflow-y-auto",
              SHEET_BODY_CLASS,
              /* Con pie, el hueco de la barra de Android lo pone el pie
                 (SHEET_FOOTER_CLASS). Sin pie no lo ponía nadie. */
              !asCentered && !footer && "pb-[max(18px,var(--safe-bottom))]",
            )}
          >
            <SheetSettledProvider open={open}>
              {skeleton && (loading || !settled) ? skeleton : children}
            </SheetSettledProvider>
          </div>
          {footerEl}
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}
